import React from 'react';
import Hero from '../components/Hero';
import { Link } from 'react-router-dom';
import { Paintbrush, Fingerprint, Sparkles, Image, CreditCard } from 'lucide-react';
import AdLeaderboard from '../components/AdLeaderboard';
import AdLeaderboardHero from '../components/AdLeaderboardHero';
import AdLeaderboard2 from '../components/AdLeaderboard2';
import DesignCard from '../components/DesignCard';
import { useLanguage } from '../contexts/LanguageContext';
import { Helmet } from 'react-helmet';

const FEATURED_DESIGNS = [
  {
    id: 1,
    imageUrl: '/images/tattobutterfly.png',
    title: 'Minimalist Wolf',
    category: 'Tattoo',
  },
  {
    id: 2,
    imageUrl: '/images/nails1.png',
    title: 'Pastel Gradient',
    category: 'Nail Art',
  },
  {
    id: 3,
    imageUrl: '/images/tattoleon.png',
    title: 'Geometric Lion',
    category: 'Tattoo',
  },
  {
    id: 4,
    imageUrl: '/images/nails2.png',
    title: 'French Tips',
    category: 'Nail Art',
  },
];

const HomePage: React.FC = () => {
  const { t, language } = useLanguage();
  
  return (
    <div>
      {/* Page-specific SEO */}
      <Helmet>
        <title>
          {language === 'en' 
            ? "TattooNailGenius - AI-Powered Tattoo & Nail Design Generator"
            : "TattooNailGenius - Generador de Diseños de Tatuajes y Uñas con IA"}
        </title>
        <meta 
          name="description" 
          content={language === 'en' 
            ? "Create stunning tattoo and nail art designs instantly with our AI-powered design platform. Free to use, no artistic skills required."
            : "Crea impresionantes diseños de tatuajes y arte de uñas al instante con nuestra plataforma de diseño potenciada por IA. Gratis, sin necesidad de habilidades artísticas."}
        />
        <link 
          rel="canonical" 
          href={language === 'en' 
            ? "https://tattoonailgenius.com/"
            : "https://tattoonailgenius.com/?lang=es"}
        />
      </Helmet>
      
      {/* Hero Section */}
      <Hero />
      
      {/* New Leaderboard Ad Banner below the hero */}
      <div className="container mx-auto px-4 py-8">
        <AdLeaderboardHero />
      </div>

      {/* Features Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('home.features.title')}
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('home.features.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700 transition-all hover:shadow-xl hover:shadow-purple-900/10">
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('home.features.aiPowered.title')}</h3>
              <p className="text-gray-400">
                {t('home.features.aiPowered.description')}
              </p>
            </div>
            
            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700 transition-all hover:shadow-xl hover:shadow-purple-900/10">
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <Image className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('home.features.unlimitedStyles.title')}</h3>
              <p className="text-gray-400">
                {t('home.features.unlimitedStyles.description')}
              </p>
            </div>
            
            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700 transition-all hover:shadow-xl hover:shadow-purple-900/10">
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <CreditCard className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('home.features.completelyFree.title')}</h3>
              <p className="text-gray-400">
                {t('home.features.completelyFree.description')}
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Designs */}
      <section className="py-20 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <h2 className="text-3xl font-bold mb-4 md:mb-0">{t('home.featuredDesigns.title')}</h2>
            <Link 
              to="/gallery" 
              className="text-purple-500 hover:text-purple-400 transition-colors font-medium"
            >
              {t('home.featuredDesigns.viewAll')}
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURED_DESIGNS.map((design) => (
              <DesignCard
                key={design.id}
                imageUrl={design.imageUrl}
                title={design.title}
                category={design.category}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Second Leaderboard Ad Banner - before CTA section */}
      <div className="container mx-auto px-4 py-8">
        <AdLeaderboard2 />
      </div>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-900 to-pink-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/nails3.png')] bg-cover bg-center opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('home.cta.title')}
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              {t('home.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/tattoo-design"
                className="bg-white hover:bg-gray-100 text-gray-900 px-8 py-3 rounded-full font-medium flex items-center justify-center gap-2 transition-all"
              >
                <Paintbrush className="h-5 w-5" />
                {t('home.cta.createTattoo')}
              </Link>
              <Link
                to="/nail-design"
                className="bg-transparent hover:bg-white/10 text-white px-8 py-3 rounded-full font-medium flex items-center justify-center gap-2 transition-all border border-white"
              >
                <Fingerprint className="h-5 w-5" />
                {t('home.cta.createNail')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;